import React from 'react';
import { ensureLocales } from './i18n';
import { LazyLoadBoundary, LazyLoadError } from '@/components/LazyLoadBoundary';

type Factory = Parameters<typeof React.lazy>[0];

/**
 * Like React.lazy, but also loads every locale namespace the entry bundle leaves out, so
 * the page never renders raw keys. Pass `namespaces` for a public page that needs only a few.
 * The start page uses plain React.lazy: it only needs the namespaces that ship eagerly.
 *
 * Load failures (chunk or locale) are wrapped in LazyLoadError and shown by LazyLoadBoundary,
 * whose Retry reloads the page: Chromium caches a failed dynamic import (the chunk and its
 * dependencies) for the life of the document, so a reload is the only reliable recovery.
 * Any other error still reaches AppErrorBoundary.
 */
export function lazyWithLocales(factory: Factory, namespaces?: readonly string[]) {
  const Lazy = React.lazy(() =>
    Promise.all([factory(), ensureLocales(namespaces)])
      .then(([m]) => m)
      .catch((err) => {
        throw new LazyLoadError(err);
      }),
  );
  const Route = (props: Record<string, unknown>) => (
    <LazyLoadBoundary>
      <Lazy {...props} />
    </LazyLoadBoundary>
  );
  return Route;
}
